import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout, { scoreColor, eligBadge } from "./AdminLayout";
import api, { API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../../components/ui/dialog";
import { Progress } from "../../components/ui/progress";
import { ADMIN_STATUSES, STATUS_LABELS, SKILL_KEYS } from "../../lib/options";
import { ArrowLeft, BrainCircuit, Download, FileText, Sparkles, Edit3, CheckCircle2, AlertTriangle, ShieldAlert, CalendarClock, Trash2 } from "lucide-react";

const INTERVIEW_CATS = [
  ["ethnic_knowledge","Ethnic market knowledge"],["commercial_thinking","Commercial thinking"],["new_business","New business ability"],
  ["customer_relationships","Customer relationships"],["negotiation","Negotiation"],["territory_planning","Territory planning"],
  ["communication","Communication"],["brand_fit","KERA brand fit"],["leadership","Leadership potential"],
];

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.admin_role === "reviewer";
  const isSuper = user?.admin_role === "super_admin";
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [override, setOverride] = useState({ score: "", reason: "" });
  const [sched, setSched] = useState({ interview_date: "", interview_time: "", interview_location: "", recruiter_contact: "careers@kerafoods.ie" });
  const [interview, setInterview] = useState({ scores: {}, strengths: "", concerns: "", recommendation: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/candidates/${id}`);
    setD(data);
    if (data.interview) setInterview(data.interview);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <AdminLayout title="Candidate"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  const { application: app, profile: p, analysis: an, cv, notes } = d;
  const score = an.override_score != null ? an.override_score : an.total;

  const runAnalysis = async () => {
    setAnalyzing(true);
    toast.loading("Running AI analysis…", { id: "ai" });
    try { await api.post(`/admin/candidates/${id}/analyze`); toast.success("Analysis complete", { id: "ai" }); await load(); }
    catch { toast.error("Analysis failed", { id: "ai" }); }
    finally { setAnalyzing(false); }
  };
  const changeStatus = async (status) => { await api.put(`/admin/candidates/${id}/status`, { status }); toast.success(`Status → ${STATUS_LABELS[status]}`); load(); };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/admin/candidates/${id}/notes`, { note }); setNote(""); toast.success("Note added"); load(); };
  const saveOverride = async () => {
    if (!override.reason.trim()) return toast.error("A written reason is required");
    await api.post(`/admin/candidates/${id}/override-score`, { score: Number(override.score), reason: override.reason });
    toast.success("Score overridden"); load();
  };
  const saveInterview = async () => {
    if (!interview.recommendation) return toast.error("Select a recommendation");
    await api.post(`/admin/candidates/${id}/interview`, interview); toast.success("Scorecard saved"); load();
  };
  const scheduleInterview = async () => {
    if (!sched.interview_date || !sched.interview_time || !sched.interview_location) return toast.error("Please provide date, time and location/link");
    await api.post(`/admin/candidates/${id}/schedule-interview`, sched);
    toast.success("Interview scheduled — email prepared"); load();
  };
  const deleteCandidate = async () => {
    setDeleting(true);
    try {
      await api.delete(`/admin/candidates/${id}`);
      toast.success("Candidate profile deleted");
      navigate("/admin/candidates");
    } catch (e) {
      toast.error("Could not delete candidate");
    } finally { setDeleting(false); }
  };

  return (
    <AdminLayout title="Candidate Match Analysis" actions={<Button variant="ghost" onClick={() => navigate("/admin/candidates")} className="text-[#C79A3B] hover:bg-white/5"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>}>
      {/* Header card */}
      <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-6 flex flex-col md:flex-row gap-6 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-extrabold text-[#101820]">{p.first_name} {p.last_name}</h2>
          <p className="text-muted-foreground">{p.current_job_title} · {p.current_employer}</p>
          <p className="text-sm text-muted-foreground mt-1 font-mono-data">{app.reference} · {p.county}, {p.country_residence}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <span className={`text-xs px-3 py-1 rounded-full ${eligBadge(an.eligibility?.status)}`} data-testid="detail-eligibility-badge">{an.eligibility?.label}</span>
            <span className="text-xs px-3 py-1 rounded-full bg-[#101820]/5 text-[#101820] font-medium">{STATUS_LABELS[app.status]}</span>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <div data-testid="ai-match-fit-score-display" className={`w-28 h-28 rounded-full grid place-items-center border-4 ${scoreColor(score)}`}>
            <div className="text-center"><div className="font-mono-data text-3xl font-extrabold">{score}</div><div className="text-[10px] uppercase tracking-wider">/ 100</div></div>
          </div>
          <div className="mt-2 font-heading font-bold text-[#101820]">{an.match_label}</div>
          {an.override_score != null && <div className="text-[10px] text-[#C76A32]">Overridden · AI was {an.total}</div>}
        </div>
      </div>

      {/* Status + actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={app.status} onValueChange={changeStatus}>
          <SelectTrigger data-testid="status-select" disabled={readOnly} className="w-52 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{ADMIN_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="run-ai-analysis-button" onClick={runAnalysis} disabled={analyzing || readOnly} className="bg-[#101820] text-[#FAF6EE] rounded-full"><BrainCircuit className="h-4 w-4 mr-1" /> {analyzing ? "Analysing…" : "Run AI Analysis"}</Button>
        {!readOnly && (
        <Dialog>
          <DialogTrigger asChild><Button data-testid="override-score-button" variant="outline" className="border-[#C79A3B]/40"><Edit3 className="h-4 w-4 mr-1" /> Override score</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Override match score</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>New score (0-100)</Label><Input data-testid="override-score-input" type="number" min={0} max={100} value={override.score} onChange={(e) => setOverride({ ...override, score: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Written reason (required)</Label><Textarea data-testid="override-reason-input" value={override.reason} onChange={(e) => setOverride({ ...override, reason: e.target.value })} className="mt-1.5" rows={3} /></div>
              <Button data-testid="save-override-button" onClick={saveOverride} className="w-full bg-[#101820] text-[#FAF6EE]">Save override</Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
        {!readOnly && (
        <Dialog>
          <DialogTrigger asChild><Button data-testid="schedule-interview-button" variant="outline" className="border-[#C79A3B]/40"><CalendarClock className="h-4 w-4 mr-1" /> Schedule interview</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Schedule interview</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Sets status to Interview and prepares a fully filled-in interview email (simulated).</p>
              <div><Label>Date</Label><Input data-testid="schedule-date-input" type="date" value={sched.interview_date} onChange={(e) => setSched({ ...sched, interview_date: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Time</Label><Input data-testid="schedule-time-input" type="time" value={sched.interview_time} onChange={(e) => setSched({ ...sched, interview_time: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Location / Teams link</Label><Input data-testid="schedule-location-input" value={sched.interview_location} onChange={(e) => setSched({ ...sched, interview_location: e.target.value })} placeholder="Dublin HQ or https://teams.microsoft.com/…" className="mt-1.5" /></div>
              <div><Label>Recruiter contact</Label><Input data-testid="schedule-contact-input" value={sched.recruiter_contact} onChange={(e) => setSched({ ...sched, recruiter_contact: e.target.value })} className="mt-1.5" /></div>
              <Button data-testid="save-schedule-button" onClick={scheduleInterview} className="w-full bg-[#101820] text-[#FAF6EE]">Confirm &amp; prepare interview email</Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
        {cv && <a href={`${API}/files/cv/${cv.id}/download?auth=${localStorage.getItem("kera_token")}`} target="_blank" rel="noreferrer"><Button data-testid="admin-cv-download" variant="outline" className="border-[#C79A3B]/40"><Download className="h-4 w-4 mr-1" /> CV</Button></a>}
        {isSuper && (
        <Dialog>
          <DialogTrigger asChild><Button data-testid="delete-candidate-button" variant="outline" className="border-[#C0392B]/40 text-[#C0392B] hover:bg-[#C0392B]/5 hover:text-[#C0392B] ml-auto"><Trash2 className="h-4 w-4 mr-1" /> Delete candidate</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Delete candidate profile</DialogTitle>
              <DialogDescription>This permanently erases the candidate and all associated data. This action cannot be undone.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-[#101820]/80">This permanently removes <b>{p.first_name} {p.last_name}</b> and all associated data — application, AI analysis, recruiter notes, interview records and CV. This action cannot be undone.</p>
              <Button data-testid="confirm-delete-candidate-button" onClick={deleteCandidate} disabled={deleting} className="w-full bg-[#C0392B] text-white hover:bg-[#a5301f]">{deleting ? "Deleting…" : "Yes, permanently delete this candidate"}</Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {readOnly && (
        <div data-testid="reviewer-readonly-banner" className="bg-[#101820]/5 border border-[#101820]/15 rounded-lg p-3 text-xs text-[#101820]/70">
          You are signed in as a <b>Panel Reviewer</b> — this is a read-only view. You can review profiles, scores and scorecards but cannot change data.
        </div>
      )}

      <div className="bg-[#C79A3B]/10 border border-[#C79A3B]/30 rounded-lg p-3 text-xs text-[#101820]/80 flex gap-2 items-start">
        <ShieldAlert className="h-4 w-4 text-[#8a6a1e] shrink-0 mt-0.5" />
        Candidate Match Score is a recruitment support tool. Final hiring decisions must be made by authorised KERA personnel. Scores never use age, gender, ethnicity, nationality, religion or other protected characteristics.
      </div>

      <Tabs defaultValue="analysis">
        <TabsList className="bg-[#101820]/5">
          <TabsTrigger value="analysis" data-testid="tab-analysis">AI Analysis</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="answers" data-testid="tab-answers">Answers</TabsTrigger>
          <TabsTrigger value="interview" data-testid="tab-interview">Interview</TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="analysis" className="space-y-4 mt-4">
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 space-y-4">
              <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
                <h3 className="font-heading font-semibold text-[#101820] mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#C79A3B]" /> AI Summary</h3>
                <p className="text-sm text-[#101820]/80 leading-relaxed">{an.summary || "Run AI Analysis to generate a narrative summary."}</p>
                <p className="text-[10px] text-muted-foreground mt-3 font-mono-data">Model: {an.model} · {an.analysed_at ? new Date(an.analysed_at).toLocaleString() : ""}</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg border border-[#2E7D32]/30 shadow-sm p-5">
                  <h3 className="font-heading font-semibold text-[#2E7D32] mb-2 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Top Strengths</h3>
                  <ul className="space-y-1 text-sm text-[#101820]/80 list-disc list-inside">{(an.strengths || []).map((s, i) => <li key={`strength-${i}-${s}`}>{s}</li>)}</ul>
                </div>
                <div className="bg-white rounded-lg border border-[#C76A32]/30 shadow-sm p-5">
                  <h3 className="font-heading font-semibold text-[#C76A32] mb-2 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Gaps / Risks</h3>
                  <ul className="space-y-1 text-sm text-[#101820]/80 list-disc list-inside">{(an.gaps || []).map((s, i) => <li key={`gap-${i}-${s}`}>{s}</li>)}</ul>
                </div>
              </div>
              <div className="bg-[#101820] rounded-lg p-5 text-[#FAF6EE] flex items-center justify-between">
                <span className="text-sm text-[#FAF6EE]/70">Recommended Action</span>
                <span className="font-heading font-bold text-[#C79A3B]">{an.recommended_action}</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
                <h3 className="font-heading font-semibold text-[#101820] mb-4">Score Breakdown (100-pt model)</h3>
                <div className="space-y-3">
                  {(an.breakdown || []).map((b) => (
                    <div key={b.criterion} data-testid={`breakdown-${b.criterion.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                      <div className="flex justify-between text-xs mb-1"><span className="text-[#101820]/80">{b.criterion}</span><span className="font-mono-data font-semibold">{b.points}/{b.max}</span></div>
                      <Progress value={(b.points / b.max) * 100} className="h-1.5" />
                      <p className="text-[11px] text-muted-foreground mt-1">{b.evidence}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-4">
          {app.application_type === "internship" ? (
          <div className="grid md:grid-cols-2 gap-4">
            <ProfileCard title="Eligibility" rows={[["Currently studying (2026)", p.currently_studying],["Right to work in Ireland", p.right_to_work],["Resident in Ireland", p.resident_ireland],["Based in", `${p.study_city || ""} ${p.county || ""}`.trim()]]} />
            <ProfileCard title="Study" rows={[["University / College", p.institution],["Course", p.course],["Year of study", p.year_of_study],["Expected graduation", p.expected_graduation]]} />
            <ProfileCard title="Availability" rows={[["Availability", (p.availability||[]).join(", ")]]} />
            <ProfileCard title="Experience & Tools" rows={[["Relevant experience", (p.relevant_experience||[]).join(", ")],["Digital tools", (p.digital_tools||[]).join(", ")]]} />
          </div>
          ) : (
          <div className="grid md:grid-cols-2 gap-4">
            <ProfileCard title="Eligibility" rows={[["Resident in Ireland", p.resident_ireland],["Right to work (no sponsorship)", p.right_to_work],["Work authorisation", p.work_auth_type],["Driving licence", p.driving_licence],["Licence category", p.licence_category],["Licence expiry", p.licence_expiry],["Willing to travel", p.willing_travel]]} />
            <ProfileCard title="Experience" rows={[["Total sales yrs", p.years_total_sales],["Field sales yrs", p.years_field_sales],["Food/FMCG yrs", p.years_food_fmcg],["Ethnic distribution yrs", p.years_ethnic_distribution],["Industry", p.current_industry],["Revenue portfolio", p.revenue_portfolio],["Active accounts", p.active_accounts],["Opened new accounts", p.opened_new_accounts],["Opened (24m)", p.accounts_opened_24m]]} />
            <ProfileCard title="Channels & Network" rows={[["Channels", (p.channels||[]).join(", ")],["Categories", (p.ethnic_categories||[]).join(", ")],["Network", (p.network_relationships||[]).join(", ")],["CRM", (p.crm_systems||[]).join(", ")]]} />
            <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
              <h3 className="font-heading font-semibold text-[#101820] mb-3">Commercial Skills</h3>
              {SKILL_KEYS.map((s) => (
                <div key={s.key} className="flex justify-between items-center py-1 text-sm"><span className="text-[#101820]/80">{s.label}</span><span className="font-mono-data font-semibold">{p.commercial_skills?.[s.key] || 0}/5</span></div>
              ))}
              <div className="mt-3 pt-3 border-t border-[#C79A3B]/15 text-sm"><span className="text-muted-foreground">Network description:</span><p className="text-[#101820]/80 mt-1">{p.network_description || "—"}</p></div>
            </div>
          </div>
          )}
          {cv?.parsed?.summary && (
            <div className="mt-4 bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
              <h3 className="font-heading font-semibold text-[#101820] mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-[#C79A3B]" /> Parsed CV Summary</h3>
              <p className="text-sm text-[#101820]/80">{cv.parsed.summary}</p>
              {cv.parsed.detected && Object.keys(cv.parsed.detected).length > 0 && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs">
                  {Object.entries(cv.parsed.detected).map(([k, v]) => v && (
                    <div key={k} className="flex justify-between border-b border-[#C79A3B]/10 py-1"><span className="text-muted-foreground capitalize">{k.replace(/_/g,' ')} (CV detected)</span><span className="text-[#101820] font-medium text-right">{Array.isArray(v) ? v.join(", ") : String(v)}</span></div>
                  ))}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="answers" className="mt-4 space-y-3">
          {(app.application_type === "internship"
            ? [["q_why","Why build your sales & marketing career with KERA?"],["q_challenge","Outreach challenge: introduce KERA to 10 Dublin retailers without a car"]]
            : [["q1","Irish ethnic market experience"],["q2","New customer/territory developed"],["q3","6-month growth plan"],["q4","Distributor/wholesaler experience"],["q5","Relationships & market knowledge"],["q6","Expected salary"],["q7","Notice period"]]
          ).map(([k, label]) => (
            <div key={k} className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-4">
              <div className="text-xs uppercase tracking-wider text-[#8a6a1e] font-semibold mb-1">{label}</div>
              <p className="text-sm text-[#101820]/80 whitespace-pre-wrap">{app.answers?.[k] || "—"}</p>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="interview" className="mt-4">
          <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
            <h3 className="font-heading font-semibold text-[#101820] mb-4">Interview Scorecard (1–5)</h3>
            <div className="grid sm:grid-cols-2 gap-x-8">
              {INTERVIEW_CATS.map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-[#C79A3B]/10">
                  <span className="text-sm text-[#101820]">{label}</span>
                  <div className="flex gap-1">{[1,2,3,4,5].map((n) => (
                    <button key={n} data-testid={`interview-${key}-${n}`} onClick={() => setInterview({ ...interview, scores: { ...interview.scores, [key]: n } })}
                      className={`w-7 h-7 rounded text-xs font-semibold ${(interview.scores?.[key]||0) >= n ? "bg-[#C79A3B] text-[#101820]" : "bg-white border border-[#C79A3B]/25 text-muted-foreground"}`}>{n}</button>
                  ))}</div>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><Label>Strengths</Label><Textarea data-testid="interview-strengths" value={interview.strengths} onChange={(e) => setInterview({ ...interview, strengths: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Concerns</Label><Textarea data-testid="interview-concerns" value={interview.concerns} onChange={(e) => setInterview({ ...interview, concerns: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1 max-w-xs"><Label>Recommendation</Label>
                <Select value={interview.recommendation} onValueChange={(v) => setInterview({ ...interview, recommendation: v })}>
                  <SelectTrigger data-testid="interview-recommendation" className="mt-1.5"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{["Strong Hire","Hire","Consider","No Hire"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button data-testid="interview-scorecard-save-button" onClick={saveInterview} disabled={readOnly} className="bg-[#101820] text-[#FAF6EE]">Save scorecard</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notes" className="mt-4">
          <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
            <h3 className="font-heading font-semibold text-[#101820] mb-2">Recruiter Notes <span className="text-xs text-muted-foreground font-normal">(internal only — never shown to candidates)</span></h3>
            <div className="flex gap-2">
              <Textarea data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" rows={2} disabled={readOnly} className="flex-1" />
              <Button data-testid="add-note-button" onClick={addNote} disabled={readOnly} className="bg-[#C79A3B] text-[#101820] self-end">Add</Button>
            </div>
            <div className="mt-4 space-y-3">
              {(notes || []).map((n) => (
                <div key={n.id} className="border-l-2 border-[#C79A3B] pl-3 py-1">
                  <div className="text-xs text-muted-foreground">{n.author} · {new Date(n.at).toLocaleString()}</div>
                  <p className="text-sm text-[#101820]/85">{n.note}</p>
                </div>
              ))}
              {(!notes || notes.length === 0) && <p className="text-sm text-muted-foreground">No notes yet.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}

function ProfileCard({ title, rows }) {
  return (
    <div className="bg-white rounded-lg border border-[#E4C675]/50 shadow-sm p-5">
      <h3 className="font-heading font-semibold text-[#101820] mb-3">{title}</h3>
      {rows.map(([l, v]) => (
        <div key={l} className="flex justify-between gap-4 py-1 text-sm border-b border-[#C79A3B]/10 last:border-0"><span className="text-muted-foreground">{l}</span><span className="text-[#101820] font-medium text-right">{v || "—"}</span></div>
      ))}
    </div>
  );
}
